/**
 * letterCoreRenderers.tsx — silnik formatek: nadpisania pól core Pisma (R3,
 * mirror `documentEngineConfig.tsx` — pilot commercial/internal).
 *
 * Pismo jest bare-core (13 pól core w tabeli `documents`, identyczny ZESTAW co
 * commercial/internal — patrz seed `apps/api/lib/seed-system-modules.ts`, slug
 * 'letters'), ale ma 3 WŁASNE zdolności silnika (SILNIK-FORMATEK-ROLLOUT.md §R3):
 *
 *  - `status`: enum WŁASNY (`LETTER_STATUSES` z `@saas/shared`), różny od
 *    `STANDARD_DOCUMENT_STATUSES` commercial/internal (draft/active/archived/rejected) —
 *    pismo ma draft/sent/received/archived/rejected.
 *  - `source`: READ-ONLY NA SZTYWNO, niezależnie od `editable` — mina
 *    behawioralna R3 (silnik natywnie uedytowalniłby pole typu 'select' bez
 *    nadpisania); nadpisanie utrzymuje disable = parytet 1:1 z LetterDataTab
 *    przed migracją (`onChange={() => {}}`, `disabled` bez warunku).
 *  - `externalId`/`externalSource`: disable warunkowy cross-field — poza
 *    create, edytowalne TYLKO gdy `source === 'manual'` (parytet 1:1 z
 *    LetterDataTab). Silnik nie ma natywnego mechanizmu takiej reguły — tylko
 *    nadpisanie ma dostęp do `ctx.form`/`ctx.isCreate`.
 *  - `urgency`: DEDYKOWANY renderer (NIE wspólny `UrgencySelect`) — enum
 *    identyczny (low/normal/high/urgent), ale etykiety z
 *    `tenant.documents.letters.detail.urgencyValues.*` (nijaka forma „Pilne"
 *    dla „pismo"), NIE z `tenant.documents.shared.urgencyValues.*` (żeńska
 *    „Pilna", którą wspólny UrgencySelect brałby dla pisma — regresja
 *    gramatyczna, §4.5). `status` analogicznie (enum + etykiety własne pism).
 *  - `system_number`: read-only NA SZTYWNO (auto-generowany przez backend),
 *    placeholder w create — ujednolicenie z commercial/internal/umowy/faktury
 *    (decyzja Alexandra R3). Etykieta z kluczy WSPÓLNYCH dokumentów
 *    (`tenant.documents.shared.fields.systemNumber` + `…systemNumberPlaceholder`),
 *    jak pilot.
 *
 * `ownerUserId` reużywa WSPÓLNY `UserPicker` (jak pilot).
 *
 * Etykiety tych nadpisań zostają na i18n — mirror `documentEngineConfig.tsx`
 * (etykieta niesiona w JSX nadpisania, poza `labelSource`). `labelSource:'definition'`
 * (Z3, `letterEngineConfig.ts`) dotyczy WYŁĄCZNIE pól z natywną delegacją
 * (title/documentDate/description/notes/rejectionReason) — te etykiety idą
 * z definicji pola (field.name), nie z i18n.
 */

import { DOCUMENT_URGENCIES, LETTER_STATUSES } from '@saas/shared';

import { TextInput } from '@/components/documents/documentCoreRenderers';
import { Dropdown } from '@/components/Dropdown';
import { Field } from '@/components/kartoteki/CoreFieldLayout';
import type { FieldRenderer } from '@/components/kartoteki/engine/engine-config';
import { UserPicker } from '@/components/ui/UserPicker';
import { UserTablePickerButton } from '@/components/ui/UserTablePickerButton';

import type { LetterFormData } from './letterFormTypes';

// Kody statusu (własny enum pism) i pilności (wspólne cztery kody dokumentów) pochodzą z
// `@saas/shared` — jedno źródło dla formularza, kolumny i filtra listy. Etykiety pilności są
// czytane z kluczy PISM (patrz nagłówek pliku, finding A — regresja gramatyczna).

/**
 * Numer systemowy — read-only zawsze (auto-generowany przez backend, niezależnie
 * od `editable`); placeholder tylko w create. Mirror `systemNumberRenderer`
 * pilota (documentEngineConfig.tsx), etykieta z kluczy wspólnych dokumentów.
 */
export const letterSystemNumberRenderer: FieldRenderer<LetterFormData> = ({
  form,
  isCreate,
  testIdPrefix,
  t,
}) => (
  <Field label={t('tenant.documents.shared.fields.systemNumber')}>
    <TextInput
      testId={`${testIdPrefix}-systemNumber`}
      value={form.systemNumber ?? ''}
      onChange={() => {}}
      disabled
      placeholder={isCreate ? t('tenant.documents.shared.systemNumberPlaceholder') : ''}
    />
  </Field>
);

export const letterStatusRenderer: FieldRenderer<LetterFormData> = ({
  form,
  setField,
  editable,
  testIdPrefix,
  hasError,
  getError,
  t,
}) => (
  <Field
    label={t('tenant.documents.letters.detail.fields.status')}
    required
    error={getError?.('status')}
  >
    <Dropdown
      options={LETTER_STATUSES.map((s) => ({
        value: s,
        label: t(`tenant.documents.letters.detail.statusValues.${s}`, { defaultValue: s }),
      }))}
      value={form.status}
      onChange={(v) => setField({ status: v })}
      disabled={!editable}
      className={hasError?.('status') ? 'border-red-500 ring-1 ring-red-500' : undefined}
      data-testid={`${testIdPrefix}-status`}
    />
  </Field>
);

export const letterUrgencyRenderer: FieldRenderer<LetterFormData> = ({
  form,
  setField,
  editable,
  testIdPrefix,
  hasError,
  getError,
  t,
}) => (
  <Field
    label={t('tenant.documents.letters.detail.fields.urgency')}
    required
    error={getError?.('urgency')}
  >
    <Dropdown
      options={DOCUMENT_URGENCIES.map((u) => ({
        value: u,
        label: t(`tenant.documents.letters.detail.urgencyValues.${u}`, { defaultValue: u }),
      }))}
      value={form.urgency}
      onChange={(v) => setField({ urgency: v })}
      disabled={!editable}
      className={hasError?.('urgency') ? 'border-red-500 ring-1 ring-red-500' : undefined}
      data-testid={`${testIdPrefix}-urgency`}
    />
  </Field>
);

/**
 * source — READ-ONLY na sztywno niezależnie od `editable` (patrz nagłówek
 * pliku, mina behawioralna R3). `onChange` no-op — parytet 1:1 z
 * LetterDataTab przed migracją.
 */
export const letterSourceRenderer: FieldRenderer<LetterFormData> = ({ form, testIdPrefix, t }) => (
  <Field label={t('tenant.documents.letters.detail.fields.source')}>
    <TextInput testId={`${testIdPrefix}-source`} value={form.source} onChange={() => {}} disabled />
  </Field>
);

/**
 * externalId — disable warunkowy cross-field (patrz nagłówek pliku): poza
 * create, edytowalne TYLKO gdy `source === 'manual'`.
 */
export const letterExternalIdRenderer: FieldRenderer<LetterFormData> = ({
  form,
  setField,
  editable,
  isCreate,
  testIdPrefix,
  hasError,
  getError,
  t,
}) => (
  <Field
    label={t('tenant.documents.letters.detail.fields.externalId')}
    error={getError?.('externalId')}
  >
    <TextInput
      testId={`${testIdPrefix}-externalId`}
      value={form.externalId ?? ''}
      onChange={(v) => setField({ externalId: v || null })}
      disabled={!editable || (!isCreate && form.source !== 'manual')}
      maxLength={200}
      invalid={hasError?.('externalId')}
    />
  </Field>
);

/** externalSource — sama reguła disable jak externalId (patrz nagłówek pliku). */
export const letterExternalSourceRenderer: FieldRenderer<LetterFormData> = ({
  form,
  setField,
  editable,
  isCreate,
  testIdPrefix,
  hasError,
  getError,
  t,
}) => (
  <Field
    label={t('tenant.documents.letters.detail.fields.externalSource')}
    error={getError?.('externalSource')}
  >
    <TextInput
      testId={`${testIdPrefix}-externalSource`}
      value={form.externalSource ?? ''}
      onChange={(v) => setField({ externalSource: v || null })}
      disabled={!editable || (!isCreate && form.source !== 'manual')}
      maxLength={100}
      invalid={hasError?.('externalSource')}
    />
  </Field>
);

export const letterOwnerUserIdRenderer: FieldRenderer<LetterFormData> = ({
  form,
  setField,
  editable,
  testIdPrefix,
  companyId,
  t,
}) => (
  <Field label={t('tenant.documents.letters.detail.fields.ownerUserId')}>
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">
        <UserPicker
          value={form.ownerUserId}
          onChange={(v) => setField({ ownerUserId: v })}
          companyId={companyId}
          disabled={!editable}
          data-testid={`${testIdPrefix}-ownerUserId`}
        />
      </div>
      <UserTablePickerButton
        value={form.ownerUserId}
        onPick={(id) => setField({ ownerUserId: id })}
        companyId={companyId}
        disabled={!editable}
        data-testid={`${testIdPrefix}-ownerUserId-open-picker`}
      />
    </div>
  </Field>
);
